import {
  WEBSITE_MEDIA_LIMITS,
  isRejectedVideoContainer,
  websiteMediaKindForMime,
} from '../lib/website-templates/media-policy';
import { validateStorefrontMediaFile } from '../lib/storefront-engine/storefront-media-validation';
import type { SupabaseClient } from '../supabase/client';
import type {
  WebsiteMediaKind,
  WebsiteMediaUploadResult,
  WebsiteSlotSchema,
} from '../types/website-templates';
import {
  parseStorefrontMediaMutationAsset,
  uploadStorefrontMediaAsset,
} from './storefront-media-assets.service';

/**
 * Suffix of `admin.websiteTemplates.media.error.<key>`. The server decides;
 * the checks made here only spare the merchant an upload that cannot pass.
 */
export type WebsiteMediaUploadErrorKey =
  | 'type'
  | 'mov'
  | 'size'
  | 'dimensions'
  | 'duration'
  | 'codec'
  | 'metadata'
  /** The bytes are not what the file says it is: damaged or mislabeled. */
  | 'corrupt'
  /** The request did not reach the server or its answer did not arrive. Shown as `generic`. */
  | 'network'
  /** The caller aborted the upload. Nothing to show. */
  | 'aborted'
  | 'generic';

/** C12: `uploading` while bytes leave the browser, `checking` until the server answers. */
export type WebsiteMediaUploadPhase = 'uploading' | 'checking';

export interface WebsiteMediaUploadProgress {
  readonly phase: WebsiteMediaUploadPhase;
  /** 0..1. Always 1 while `checking`. */
  readonly fraction: number;
}

export class WebsiteMediaUploadError extends Error {
  readonly key: WebsiteMediaUploadErrorKey;
  /** The code the server or the local check answered with. */
  readonly code: string;

  constructor(key: WebsiteMediaUploadErrorKey, code: string) {
    super(code);
    this.name = 'WebsiteMediaUploadError';
    this.key = key;
    this.code = code;
  }
}

const ERROR_KEYS: Readonly<Record<string, WebsiteMediaUploadErrorKey>> = {
  media_invalid_type: 'type',
  image_invalid_type: 'type',
  media_required: 'type',
  image_required: 'type',
  media_too_large: 'size',
  image_too_large: 'size',
  media_too_many_frames: 'size',
  storage_quota_exceeded: 'size',
  media_dimensions_invalid: 'dimensions',
  image_dimensions_invalid: 'dimensions',
  media_too_long: 'duration',
  media_codec_unsupported: 'codec',
  media_metadata_unsupported: 'metadata',
};

export function websiteMediaUploadErrorKey(code: string): WebsiteMediaUploadErrorKey {
  return Object.prototype.hasOwnProperty.call(ERROR_KEYS, code)
    ? (ERROR_KEYS[code] ?? 'generic')
    : 'generic';
}

/**
 * The `admin.websiteTemplates.media.error.<suffix>` to show for a key. Every
 * key has a message; `network` and `aborted` share the generic one.
 */
export function websiteMediaUploadMessageKeySuffix(
  key: WebsiteMediaUploadErrorKey,
): Exclude<WebsiteMediaUploadErrorKey, 'network' | 'aborted'> {
  return key === 'network' || key === 'aborted' ? 'generic' : key;
}

/** Codes that mean "the bytes are not of the declared type". */
const CONTENT_MISMATCH_CODES: ReadonlySet<string> = new Set([
  'media_invalid_type',
  'image_invalid_type',
]);

/**
 * The key of a refusal that came AFTER the declared type was accepted. The
 * validators have no code of their own for an unreadable file: they answer
 * `*_invalid_type`, the same code as for a type that is not allowed. Once the
 * declared type has passed, that code can only mean the content does not
 * match it.
 */
function keyAfterTypeCheck(code: string): WebsiteMediaUploadErrorKey {
  return CONTENT_MISMATCH_CODES.has(code) ? 'corrupt' : websiteMediaUploadErrorKey(code);
}

const EXTENSIONS_BY_MIME: Readonly<Record<string, readonly string[]>> = {
  'image/jpeg': ['jpg', 'jpeg'],
  'image/png': ['png'],
  'image/webp': ['webp'],
  'image/gif': ['gif'],
  'video/mp4': ['mp4', 'm4v'],
  'video/webm': ['webm'],
};
const REJECTED_EXTENSIONS: readonly string[] = ['mov', 'qt'];

function extensionOf(name: string): string | null {
  const match = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return match?.[1]?.toLowerCase() ?? null;
}

/**
 * What can be told from the file's type, name and size alone, before any
 * byte is read or sent: the key of the problem, or null. It spares the
 * merchant an upload that cannot pass. The server decides (A5).
 */
export function precheckWebsiteMediaFile(
  file: File,
  schema: WebsiteSlotSchema,
): WebsiteMediaUploadErrorKey | null {
  const type = file.type.trim().toLowerCase();
  const extension = extensionOf(file.name);
  if (isRejectedVideoContainer(type)) return 'mov';
  if (extension !== null && REJECTED_EXTENSIONS.includes(extension)) return 'mov';

  const kind = websiteMediaKindForMime(type);
  if (!kind || !(schema.mediaKinds ?? []).includes(kind)) return 'type';
  // A name that says another format than the type is not trusted either way.
  if (extension !== null && !(EXTENSIONS_BY_MIME[type] ?? []).includes(extension)) return 'type';

  if (file.size === 0) return 'type';
  if (file.size > WEBSITE_MEDIA_LIMITS[kind].maxBytes) return 'size';
  return null;
}

export interface UploadWebsiteMediaInput {
  readonly storeId: string;
  readonly file: File;
  /** Kinds the slot allows (`WebsiteSlotSchema.mediaKinds`). */
  readonly allowedKinds: readonly WebsiteMediaKind[];
  readonly label?: string | null;
  readonly altText?: string | null;
  readonly supabaseUrl: string;
  /**
   * C12. With it the file is sent by `XMLHttpRequest`, the only browser API
   * that reports upload progress. Called with `uploading` 0 first, then as
   * bytes leave, then once with `checking` when the last byte has left.
   */
  readonly onProgress?: (progress: WebsiteMediaUploadProgress) => void;
  /** C12. Aborting rejects with the key `aborted`; nothing is stored. */
  readonly signal?: AbortSignal;
}

interface FunctionsClient {
  functions: {
    invoke(
      name: string,
      options: { readonly body?: FormData; readonly headers?: Record<string, string> },
    ): Promise<{ readonly data: unknown; readonly error: unknown }>;
  };
  auth: {
    getSession(): Promise<{
      readonly data: { readonly session: { readonly access_token: string } | null };
      readonly error: unknown;
    }>;
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isUuid(value: unknown): value is string {
  return (
    typeof value === 'string' &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
  );
}

function positiveInteger(value: unknown): number | null {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : null;
}

/** The `{ error }` code of a failed function call, when the answer has one. */
async function functionErrorCode(error: unknown): Promise<string | null> {
  if (!isRecord(error)) return null;
  const context = error.context;
  if (isRecord(context) && typeof context.json === 'function') {
    try {
      const body: unknown = await (context as unknown as Response).clone().json();
      if (isRecord(body) && typeof body.error === 'string') return body.error;
    } catch {
      return null;
    }
  }
  return null;
}

const MEDIA_FUNCTION_PATH = '/functions/v1/storefront-media';

interface XhrAnswer {
  readonly status: number;
  readonly body: unknown;
}

/** The key Supabase sent this client, when the client object still holds it. */
function apiKeyOf(client: SupabaseClient): string | null {
  const key = (client as unknown as { readonly supabaseKey?: unknown }).supabaseKey;
  return typeof key === 'string' && key !== '' ? key : null;
}

function parseJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return null;
  }
}

/**
 * Posts the form with upload progress. Resolves with whatever the server
 * answered, of any status. Rejects only when there is no answer: `network`
 * or `aborted`.
 */
function postWithProgress(args: {
  readonly url: string;
  readonly form: FormData;
  readonly headers: Readonly<Record<string, string>>;
  readonly onProgress?: (progress: WebsiteMediaUploadProgress) => void;
  readonly signal?: AbortSignal;
}): Promise<XhrAnswer> {
  const { signal, onProgress } = args;
  return new Promise<XhrAnswer>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new WebsiteMediaUploadError('aborted', 'aborted'));
      return;
    }

    const xhr = new XMLHttpRequest();
    let settled = false;
    let lastFraction = 0;
    let checking = false;

    const onAbortSignal = (): void => xhr.abort();
    const settle = (run: () => void): void => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener('abort', onAbortSignal);
      run();
    };
    const report = (progress: WebsiteMediaUploadProgress): void => {
      if (settled || !onProgress) return;
      // A listener that throws must not leave the upload hanging.
      try {
        onProgress(progress);
      } catch (error) {
        console.error('[websiteMediaUpload.onProgress]', error);
      }
    };
    const startChecking = (): void => {
      if (checking) return;
      checking = true;
      report({ phase: 'checking', fraction: 1 });
    };

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      if (checking || !event.lengthComputable || event.total <= 0) return;
      // Progress never runs backwards and never claims more than all of it.
      const fraction = Math.min(1, Math.max(lastFraction, event.loaded / event.total));
      lastFraction = fraction;
      report({ phase: 'uploading', fraction });
    };
    xhr.upload.onload = startChecking;
    xhr.onload = (): void => {
      startChecking();
      const answer = { status: xhr.status, body: parseJson(xhr.responseText) };
      settle(() => resolve(answer));
    };
    xhr.onerror = (): void =>
      settle(() => reject(new WebsiteMediaUploadError('network', 'network_failed')));
    xhr.ontimeout = (): void =>
      settle(() => reject(new WebsiteMediaUploadError('network', 'network_failed')));
    xhr.onabort = (): void =>
      settle(() => reject(new WebsiteMediaUploadError('aborted', 'aborted')));

    signal?.addEventListener('abort', onAbortSignal, { once: true });
    try {
      xhr.open('POST', args.url);
      for (const [name, value] of Object.entries(args.headers)) xhr.setRequestHeader(name, value);
      report({ phase: 'uploading', fraction: 0 });
      xhr.send(args.form);
    } catch {
      settle(() => reject(new WebsiteMediaUploadError('network', 'network_failed')));
    }
  });
}

/** The asset of a 2xx answer, else the error the answer names. */
function answerBody(answer: XhrAnswer): unknown {
  if (answer.status >= 200 && answer.status < 300) return answer.body;
  const code =
    isRecord(answer.body) && typeof answer.body.error === 'string'
      ? answer.body.error
      : 'upload_failed';
  throw new WebsiteMediaUploadError(keyAfterTypeCheck(code), code);
}

function parseResult(value: unknown, expectedKind: WebsiteMediaKind): WebsiteMediaUploadResult {
  const asset = isRecord(value) ? value.asset : null;
  if (!isRecord(asset)) throw new WebsiteMediaUploadError('generic', 'upload_failed');
  const width = positiveInteger(asset.width);
  const height = positiveInteger(asset.height);
  if (!isUuid(asset.mediaAssetId) || asset.kind !== expectedKind || !width || !height) {
    throw new WebsiteMediaUploadError('generic', 'upload_failed');
  }
  if (expectedKind !== 'video') {
    if (asset.durationMs !== undefined) {
      throw new WebsiteMediaUploadError('generic', 'upload_failed');
    }
    return { mediaAssetId: asset.mediaAssetId, kind: expectedKind, width, height };
  }
  const durationMs = positiveInteger(asset.durationMs);
  if (!durationMs || durationMs > WEBSITE_MEDIA_LIMITS.video.maxDurationMs) {
    throw new WebsiteMediaUploadError('generic', 'upload_failed');
  }
  return { mediaAssetId: asset.mediaAssetId, kind: 'video', width, height, durationMs };
}

/**
 * Uploads media for a website slot and answers with what the server measured.
 * The answer carries no path, bucket or file name.
 *
 * - image: the existing storefront media upload.
 * - gif, video: checked and cleaned by the `storefront-media` function before
 *   it is stored. MOV, HEVC and AV1 are refused; nothing is transcoded.
 *
 * With `onProgress` or `signal` the same form goes to the same function by
 * `XMLHttpRequest`; without them by `functions.invoke`, as before.
 */
export async function uploadWebsiteMedia(
  client: SupabaseClient,
  input: UploadWebsiteMediaInput,
): Promise<WebsiteMediaUploadResult> {
  const { file } = input;
  if (input.signal?.aborted) throw new WebsiteMediaUploadError('aborted', 'aborted');
  if (isRejectedVideoContainer(file.type)) {
    throw new WebsiteMediaUploadError('mov', 'media_codec_unsupported');
  }
  const kind = websiteMediaKindForMime(file.type);
  if (!kind || !input.allowedKinds.includes(kind)) {
    throw new WebsiteMediaUploadError('type', 'media_invalid_type');
  }
  if (file.size === 0) throw new WebsiteMediaUploadError('type', 'media_required');
  if (file.size > WEBSITE_MEDIA_LIMITS[kind].maxBytes) {
    throw new WebsiteMediaUploadError('size', 'media_too_large');
  }
  const tracked = input.onProgress !== undefined || input.signal !== undefined;

  if (kind === 'image') {
    const inspection = await validateStorefrontMediaFile(file);
    if (typeof inspection === 'string') {
      throw new WebsiteMediaUploadError(keyAfterTypeCheck(inspection), inspection);
    }
    if (tracked) {
      const form = new FormData();
      form.set('action', 'upload');
      form.set('store_id', input.storeId);
      form.set('role', 'section-image');
      form.set('image', file);
      if (input.label?.trim()) form.set('label', input.label.trim().slice(0, 120));
      if (input.altText?.trim()) form.set('alt_text', input.altText.trim().slice(0, 240));
      const body = answerBody(await postTracked(client, input, MEDIA_FUNCTION_PATH, form));
      let assetId: string;
      try {
        assetId = parseStorefrontMediaMutationAsset(body, input.supabaseUrl).id;
      } catch {
        throw new WebsiteMediaUploadError('generic', 'upload_failed');
      }
      if (!isUuid(assetId)) throw new WebsiteMediaUploadError('generic', 'upload_failed');
      return {
        mediaAssetId: assetId,
        kind: 'image',
        width: inspection.width,
        height: inspection.height,
      };
    }
    try {
      const asset = await uploadStorefrontMediaAsset(client, {
        storeId: input.storeId,
        file,
        label: input.label ?? undefined,
        altText: input.altText ?? undefined,
        supabaseUrl: input.supabaseUrl,
      });
      return {
        mediaAssetId: asset.id,
        kind: 'image',
        width: inspection.width,
        height: inspection.height,
      };
    } catch (error) {
      const code = (await functionErrorCode(error)) ?? 'upload_failed';
      throw new WebsiteMediaUploadError(keyAfterTypeCheck(code), code);
    }
  }

  const form = new FormData();
  form.set('action', 'upload-media');
  form.set('store_id', input.storeId);
  form.set('media', file);
  if (input.label?.trim()) form.set('label', input.label.trim().slice(0, 120));
  if (input.altText?.trim()) form.set('alt_text', input.altText.trim().slice(0, 240));

  if (tracked) {
    const answer = await postTracked(
      client,
      input,
      `${MEDIA_FUNCTION_PATH}?action=upload-media`,
      form,
    );
    return parseResult(answerBody(answer), kind);
  }

  const accessToken = await accessTokenOf(client);
  const { data, error } = await (client as unknown as FunctionsClient).functions.invoke(
    'storefront-media?action=upload-media',
    {
      body: form,
      headers: { authorization: `Bearer ${accessToken}`, 'x-dukanti-store-id': input.storeId },
    },
  );
  if (error) {
    const code = (await functionErrorCode(error)) ?? 'upload_failed';
    throw new WebsiteMediaUploadError(keyAfterTypeCheck(code), code);
  }
  return parseResult(data, kind);
}

async function accessTokenOf(client: SupabaseClient): Promise<string> {
  const session = await (client as unknown as FunctionsClient).auth.getSession();
  const accessToken = session.data.session?.access_token;
  if (session.error || !accessToken) {
    throw new WebsiteMediaUploadError('generic', 'permission_denied');
  }
  return accessToken;
}

async function postTracked(
  client: SupabaseClient,
  input: UploadWebsiteMediaInput,
  path: string,
  form: FormData,
): Promise<XhrAnswer> {
  const accessToken = await accessTokenOf(client);
  // The session read is awaited: the caller may have aborted meanwhile.
  if (input.signal?.aborted) throw new WebsiteMediaUploadError('aborted', 'aborted');
  const apiKey = apiKeyOf(client);
  return postWithProgress({
    url: `${input.supabaseUrl.replace(/\/+$/, '')}${path}`,
    form,
    headers: {
      authorization: `Bearer ${accessToken}`,
      'x-dukanti-store-id': input.storeId,
      ...(apiKey === null ? {} : { apikey: apiKey }),
    },
    onProgress: input.onProgress,
    signal: input.signal,
  });
}
